// src/pages/home/Home.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout           from '../../components/Layout';
import RelanceModal     from '../../components/crm/RelanceModal';
import {
  getAllRelances,
  createRelance,
  deleteRelance,
  actionOk,
  STATUT_CONFIG as PROSPECT_STATUT_CONFIG,
  NOTES_LABELS,
} from '../../services/crm/relancesService';
import {
  createEtudiantRelance,
  deleteEtudiantRelance,
  actionOkEtudiant,
  STATUT_CONFIG as ETUDIANT_STATUT_CONFIG,
} from '../../services/crm/etudiantRelancesService';
import {
  getAllDiplomeRelances,
  createDiplomeRelance,
  deleteDiplomeRelance,
  actionOkDiplome,
} from '../../services/crm/diplomeRelancesService';
import '../../styles/home/home.css';

// Fusion des configurations
const STATUT_CONFIG = { ...PROSPECT_STATUT_CONFIG, ...ETUDIANT_STATUT_CONFIG };

const TYPE_ECHANGE_OPTIONS = [
  { value: 'appel',   label: 'Appel' },
  { value: 'email',   label: 'Email' },
  { value: 'message', label: 'WhatsApp' },
];

const NOTES_OPTIONS = [
  { value: '',      label: '— Non renseigné —' },
  { value: 'chaud', label: '🔥 Très intéressé' },
  { value: 'moyen', label: '🤔 Intéressé moyen' },
  { value: 'froid', label: '❄️ Pas intéressé' },
  { value: 'perdu', label: '🚫 Perdu' },
];

export default function Home() {
  const navigate = useNavigate();

  // ── Relances ──────────────────────────────────────────────────────────────
  const [relances,       setRelances]       = useState([]);
  const [relLoading,     setRelLoading]     = useState(true);
  const [relError,       setRelError]       = useState('');
  const [relPartiel,     setRelPartiel]     = useState('');
  const [reminderFilter, setReminderFilter] = useState('all');
  const [reminderSearch, setReminderSearch] = useState('');
  const [savingId,       setSavingId]       = useState(null);

  // ── Modals ────────────────────────────────────────────────────────────────
  const [detailModal,     setDetailModal]     = useState(null);
  const [showOkPrompt,    setShowOkPrompt]    = useState(false);
  const [pendingOkId,     setPendingOkId]     = useState(null);
  const [pendingRelance,  setPendingRelance]  = useState(null);
  const [pendingType,     setPendingType]     = useState(null);

  // ── Champs du popup OK ────────────────────────────────────────────────────
  const [okTypeEchange, setOkTypeEchange] = useState('appel');
  const [okNotes,       setOkNotes]       = useState('');
  const [okContenu,     setOkContenu]     = useState('');

  const [reprogModal,     setReprogModal]     = useState(false);
  const [reprogData,      setReprogData]      = useState(null);
  const [modalLoading,    setModalLoading]    = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteType,      setDeleteType]      = useState(null);

  // ── Charger les relances ──────────────────────────────────────────────────
  // Les relances étudiants ne sont pas demandées : elles n'ont pas de partie
  // serveur (route /api/etudiants/ retirée à l'unification CRM + Lab, historique
  // désactivé dans Etudiants.jsx), et leur 404 faisait échouer tout le chargement.
  // Chaque source se charge seule : une panne n'efface plus les autres relances.
  const loadRelances = useCallback(async () => {
    setRelLoading(true);
    setRelError('');
    setRelPartiel('');

    const sources = [
      { type: 'prospect', libelle: 'prospects', charger: getAllRelances },
      { type: 'diplome',  libelle: 'diplômés',  charger: getAllDiplomeRelances },
    ];
    const resultats = await Promise.allSettled(sources.map((s) => s.charger()));
    const echecs = sources.filter((s, i) => {
      if (resultats[i].status === 'fulfilled') return false;
      console.error(`Relances des ${s.libelle} :`, resultats[i].reason);
      return true;
    });

    setRelances(resultats
      .flatMap((r, i) => (r.status === 'fulfilled' ? r.value.map((x) => ({ ...x, type: sources[i].type })) : []))
      .sort((a, b) => a.dateRelance.localeCompare(b.dateRelance)));
    if (echecs.length === sources.length) setRelError('Impossible de charger les relances.');
    else if (echecs.length) setRelPartiel(`Relances des ${echecs.map((s) => s.libelle).join(' et des ')} indisponibles pour le moment.`);
    setRelLoading(false);
  }, []);

  useEffect(() => { loadRelances(); }, [loadRelances]);

  // ── Helpers génériques ────────────────────────────────────────────────────
  const handleCreateRelance = async (type, id, { dateRelance, commentaire, formationIds }) => {
    if (type === 'prospect') return await createRelance(id, { dateRelance, commentaire, formationIds });
    if (type === 'etudiant') return await createEtudiantRelance(id, { dateRelance, commentaire, formationIds });
    return await createDiplomeRelance(id, { dateRelance, commentaire, formationIds });
  };

  const handleDeleteRelance = async (id, type) => {
    if (type === 'prospect') await deleteRelance(id);
    else if (type === 'etudiant') await deleteEtudiantRelance(id);
    else await deleteDiplomeRelance(id);
  };

  const handleActionOk = async (id, type, payload) => {
    if (type === 'prospect') return await actionOk(id, payload);
    if (type === 'etudiant') return await actionOkEtudiant(id, payload);
    return await actionOkDiplome(id, payload);
  };

  // ── Suppression ───────────────────────────────────────────────────────────
  const handleDelete = (relance) => {
    setDeleteConfirmId(relance.id);
    setDeleteType(relance.type);
  };

  const confirmDelete = async () => {
    try {
      await handleDeleteRelance(deleteConfirmId, deleteType);
      setRelances((prev) => prev.filter((r) => r.id !== deleteConfirmId));
    } catch (err) { console.error(err); }
    finally {
      setDeleteConfirmId(null);
      setDeleteType(null);
    }
  };

  // ── Ouvrir popup OK ───────────────────────────────────────────────────────
  const openOkPrompt = (relance) => {
    setPendingOkId(relance.id);
    setPendingRelance(relance);
    setPendingType(relance.type);
    setOkTypeEchange('appel');
    setOkNotes('');
    setOkContenu('');
    setShowOkPrompt(true);
  };

  // ── Confirmer l'appel ─────────────────────────────────────────────────────
  const confirmOk = async () => {
    if (!pendingOkId) return;
    try {
      setSavingId(pendingOkId);
      setShowOkPrompt(false);

      const { relance: updated } = await handleActionOk(pendingOkId, pendingType, {
        type_echange: okTypeEchange,
        notes:        okNotes,
        contenu:      okContenu,
      });

      setRelances((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...updated, type: pendingType } : r))
      );

      let targetId = null;
      let targetFormations = [];

      if (pendingType === 'prospect') {
        targetId = updated.prospectId;
        targetFormations = updated.formationsList || [];
      } else if (pendingType === 'etudiant') {
        targetId = updated.etudiantId;
        targetFormations = updated.formationsList || [];
      } else {
        targetId = updated.diplomeId;
        targetFormations = updated.formationsList || [];
      }

      setReprogData({ 
        id: targetId, 
        type: pendingType,
        formations: targetFormations
      });
      setReprogModal(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingId(null);
      setPendingOkId(null);
      setPendingRelance(null);
      setPendingType(null);
    }
  };

  // ── Reprogrammer ──────────────────────────────────────────────────────────
  const handleReprog = async ({ dateRelance, commentaire, formationIds }) => {
    if (!reprogData) return;
    try {
      setModalLoading(true);
      
      let finalFormationIds = formationIds;
      if ((!finalFormationIds || finalFormationIds.length === 0) && reprogData.formations?.length > 0) {
        finalFormationIds = reprogData.formations.map(f => f.id);
      }
      
      const newR = await handleCreateRelance(reprogData.type, reprogData.id, {
        dateRelance,
        commentaire,
        formationIds: finalFormationIds,
      });
      setRelances((prev) => [{ ...newR, type: reprogData.type }, ...prev]);
      setReprogModal(false);
      setReprogData(null);
    } catch (err) {
      console.error(err);
    } finally {
      setModalLoading(false);
    }
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const handleNavigate = (relance) => {
    if (relance.type === 'prospect')      navigate(`/prospects?id=${relance.prospectId}`);
    else if (relance.type === 'etudiant') navigate(`/etudiants?id=${relance.etudiantId}`);
    else                                  navigate(`/diplomes?id=${relance.diplomeId}`);
  };

  // ── Filtrage + tri ────────────────────────────────────────────────────────
  const filteredRelances = relances
    .filter((r) => {
      if (reminderFilter === 'en_retard')    return r.statutCalc === 'en_retard';
      if (reminderFilter === 'aujourd_hui')  return r.statutCalc === 'aujourd_hui';
      if (reminderFilter === 'a_venir')      return r.statutCalc === 'a_venir';
      if (reminderFilter === 'fait')         return r.statutCalc === 'fait';
      return true;
    })
    .filter((r) => {
      let nom, prenom, telephone;
      if (r.type === 'prospect')      { nom = r.prospectNom; prenom = r.prospectPrenom; telephone = r.prospectTelephone; }
      else if (r.type === 'etudiant') { nom = r.etudiantNom; prenom = r.etudiantPrenom; telephone = r.etudiantTelephone; }
      else                            { nom = r.diplomeNom;  prenom = r.diplomePrenom;  telephone = r.diplomeTelephone; }
      return `${prenom} ${nom}`.toLowerCase().includes(reminderSearch.toLowerCase()) ||
             (telephone || '').includes(reminderSearch);
    })
    .sort((a, b) => {
      const ORDER = { en_retard: 0, aujourd_hui: 1, a_venir: 2, fait: 3 };
      const diff  = (ORDER[a.statutCalc] ?? 4) - (ORDER[b.statutCalc] ?? 4);
      if (diff !== 0) return diff;
      return a.dateRelance.localeCompare(b.dateRelance);
    });

  // ── Compteurs par statut ──────────────────────────────────────────────────
  const countByStatut = (statut) => relances.filter(r => r.statutCalc === statut).length;
  const totalActives  = relances.filter(r => r.statutCalc !== 'fait').length;

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // ── Filtres disponibles ───────────────────────────────────────────────────
  const FILTERS = [
    { key: 'all',          label: 'Tous',         count: relances.length },
    { key: 'en_retard',    label: 'En retard',    count: countByStatut('en_retard') },
    { key: 'aujourd_hui',  label: "Aujourd'hui",  count: countByStatut('aujourd_hui') },
    { key: 'a_venir',      label: 'À venir',      count: countByStatut('a_venir') },
    { key: 'fait',         label: 'Terminées',    count: countByStatut('fait') },
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Layout>
      <div className="home-dashboard">

        {/* ══ TITRE PRINCIPAL ══ */}
        <div className="home-page-header">
          <h1 className="home-page-title">
            <i className="fa-solid fa-bell"></i> Rappels &amp; Relances
          </h1>
          {!relLoading && (
            <span className="home-total-badge">{totalActives} actives</span>
          )}
        </div>

        <div className="home-content-row">

          {/* ══════════════════════════════════════════
              VUE D'ENSEMBLE — colonne gauche
          ══════════════════════════════════════════ */}
          <aside className="overview-panel">
            <div className="overview-header">
              <i className="fa-solid fa-chart-pie"></i>
              <span>Vue d'ensemble</span>
            </div>

            <div className="overview-stats">
              <div
                className={`overview-stat-row ${reminderFilter === 'aujourd_hui' ? 'stat-active' : ''}`}
                onClick={() => setReminderFilter(reminderFilter === 'aujourd_hui' ? 'all' : 'aujourd_hui')}
              >
                <div className="stat-icon-wrap stat-today-icon">
                  <i className="fa-solid fa-sun"></i>
                </div>
                <span className="stat-label">Aujourd'hui</span>
                <span className="stat-count stat-today">{countByStatut('aujourd_hui')}</span>
              </div>

              <div
                className={`overview-stat-row ${reminderFilter === 'a_venir' ? 'stat-active' : ''}`}
                onClick={() => setReminderFilter(reminderFilter === 'a_venir' ? 'all' : 'a_venir')}
              >
                <div className="stat-icon-wrap stat-future-icon">
                  <i className="fa-solid fa-calendar-days"></i>
                </div>
                <span className="stat-label">À venir</span>
                <span className="stat-count stat-future">{countByStatut('a_venir')}</span>
              </div>

              <div
                className={`overview-stat-row ${reminderFilter === 'en_retard' ? 'stat-active' : ''}`}
                onClick={() => setReminderFilter(reminderFilter === 'en_retard' ? 'all' : 'en_retard')}
              >
                <div className="stat-icon-wrap stat-late-icon">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                </div>
                <span className="stat-label">En retard</span>
                <span className="stat-count stat-late">{countByStatut('en_retard')}</span>
              </div>

              <div
                className={`overview-stat-row ${reminderFilter === 'fait' ? 'stat-active' : ''}`}
                onClick={() => setReminderFilter(reminderFilter === 'fait' ? 'all' : 'fait')}
              >
                <div className="stat-icon-wrap stat-done-icon">
                  <i className="fa-solid fa-circle-check"></i>
                </div>
                <span className="stat-label">Terminées</span>
                <span className="stat-count stat-done">{countByStatut('fait')}</span>
              </div>
            </div>

            {/* Taux de suivi */}
            {!relLoading && relances.length > 0 && (() => {
              const pct = Math.round((countByStatut('fait') / relances.length) * 100);
              const r   = 40;
              const circ = 2 * Math.PI * r;
              const dash = (pct / 100) * circ;
              return (
                <div className="overview-donut-wrap">
                  <p className="donut-label">Taux de suivi</p>
                  <div className="donut-container">
                    <svg viewBox="0 0 100 100" className="donut-svg">
                      <circle cx="50" cy="50" r={r} fill="none" stroke="#E5E7EB" strokeWidth="10" />
                      <circle
                        cx="50" cy="50" r={r}
                        fill="none"
                        stroke="#4aa3c7"
                        strokeWidth="10"
                        strokeDasharray={`${dash} ${circ - dash}`}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <span className="donut-pct">{pct}%</span>
                  </div>
                </div>
              );
            })()}
          </aside>

          {/* ══════════════════════════════════════════
              LISTE RELANCES — colonne droite
          ══════════════════════════════════════════ */}
          <section className="reminders-panel">

            {/* Barre recherche + filtres */}
            <div className="reminder-filters">
              <input
                className="reminder-search"
                placeholder="🔍 Rechercher nom ou tél..."
                value={reminderSearch}
                onChange={(e) => setReminderSearch(e.target.value)}
              />
              <div className="filter-tabs">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className={`filter-tab ${reminderFilter === f.key ? 'active' : ''} filter-tab-${f.key}`}
                    onClick={() => setReminderFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Légende */}
            <div className="reminder-legend">
              <span className="leg late">● En retard</span>
              <span className="leg today">● Aujourd'hui</span>
              <span className="leg future">● À venir</span>
              <span className="leg donec">● Fait</span>
            </div>

            {relLoading && (
              <div className="relances-loading-home">
                <i className="fa-solid fa-spinner fa-spin"></i> Chargement des relances…
              </div>
            )}
            {relError && (
              <div className="relances-error-home">
                <i className="fa-solid fa-triangle-exclamation"></i> {relError}
                <button onClick={loadRelances} className="btn-retry">Réessayer</button>
              </div>
            )}

            {!relLoading && !relError && relPartiel && (
              <div className="relances-error-home">
                <i className="fa-solid fa-triangle-exclamation"></i> {relPartiel}
                <button onClick={loadRelances} className="btn-retry">Réessayer</button>
              </div>
            )}

            {!relLoading && !relError && (
              <div className="reminders-list">
                {filteredRelances.length === 0 && (
                  <div className="empty-reminders">Aucun rappel trouvé</div>
                )}

                {filteredRelances.map((r) => {
                  const cfg    = STATUT_CONFIG[r.statutCalc] || STATUT_CONFIG.a_venir;
                  const isDone = r.statutCalc === 'fait';
                  const isBusy = savingId === r.id;
                  let name, phone, formationsList;

                  if (r.type === 'prospect') {
                    name          = `${r.prospectPrenom} ${r.prospectNom}`;
                    phone         = r.prospectTelephone;
                    formationsList = r.formationsList || [];
                  } else if (r.type === 'etudiant') {
                    name          = `${r.etudiantPrenom} ${r.etudiantNom}`;
                    phone         = r.etudiantTelephone;
                    formationsList = r.formationsList || [];
                  } else {
                    name          = `${r.diplomePrenom} ${r.diplomeNom}`;
                    phone         = r.diplomeTelephone;
                    formationsList = r.formationsList || [];
                  }

                  return (
                    <div key={`${r.type}-${r.id}`} className={`reminder-card ${cfg.cardCls}`}>
                      <div className="reminder-left">
                        {isDone ? (
                          <button type="button" className="reminder-ok-btn ok-done" disabled>
                            <i className="fa-solid fa-circle-check"></i>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="reminder-ok-btn"
                            title="Confirmer l'appel effectué"
                            disabled={isBusy}
                            onClick={() => openOkPrompt(r)}
                          >
                            {isBusy
                              ? <i className="fa-solid fa-spinner fa-spin"></i>
                              : <i className="fa-solid fa-phone-volume"></i>
                            }
                          </button>
                        )}

                        <div className="reminder-info">
                          {/* Nom + badge type séparés */}
                          <div className="reminder-name-row">
                            <span className="reminder-name">{name}</span>
                            <span className={`reminder-type-pill type-${r.type}`}>
                              {r.type === 'prospect' ? '🎯 Prospect'
                               : r.type === 'etudiant' ? '🎓 Étudiant'
                               : '📜 Diplômé'}
                            </span>
                          </div>

                          <div className="reminder-phone">
                            <i className="fa-solid fa-phone"></i> {phone}
                          </div>
                          <div className="reminder-date">
                            <i className="fa-regular fa-calendar"></i> {fmtDate(r.dateRelance)}
                          </div>
                          
                          {formationsList && formationsList.length > 0 && (
                            <div className="reminder-formations-list">
                              {formationsList.map((f, idx) => (
                                <span key={f.id || idx} className="reminder-formation-badge">
                                  {f.intitule || f.label || f}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {r.commentaire && (
                            <div className="reminder-comment">
                              <i className="fa-regular fa-comment"></i> {r.commentaire}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="reminder-right">
                        <span className={`reminder-badge ${cfg.badgeCls}`}>{cfg.label}</span>
                        <div className="reminder-actions">
                          <button
                            type="button"
                            className="btn-detail"
                            onClick={() => setDetailModal(r)}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            type="button"
                            className="btn-delete-reminder"
                            onClick={() => handleDelete(r)}
                          >
                            <i className="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ════ MODAL DÉTAIL ════ */}
      {detailModal && (
        <div className="modal-overlay show" onClick={() => setDetailModal(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détails de la relance</h3>
              <button type="button" className="modal-close" onClick={() => setDetailModal(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-row">
                <span className="modal-label">
                  <i className="fa-solid fa-user"></i>{' '}
                  {detailModal.type === 'prospect' ? 'Prospect' : detailModal.type === 'etudiant' ? 'Étudiant' : 'Diplômé'}
                </span>
                <span className="modal-value">
                  {detailModal.type === 'prospect'
                    ? `${detailModal.prospectPrenom} ${detailModal.prospectNom}`
                    : detailModal.type === 'etudiant'
                    ? `${detailModal.etudiantPrenom} ${detailModal.etudiantNom}`
                    : `${detailModal.diplomePrenom} ${detailModal.diplomeNom}`}
                </span>
              </div>
              <div className="modal-row">
                <span className="modal-label"><i className="fa-solid fa-phone"></i> Téléphone</span>
                <span className="modal-value">
                  {detailModal.type === 'prospect'
                    ? detailModal.prospectTelephone
                    : detailModal.type === 'etudiant'
                    ? detailModal.etudiantTelephone
                    : detailModal.diplomeTelephone}
                </span>
              </div>
              <div className="modal-row">
                <span className="modal-label"><i className="fa-regular fa-calendar"></i> Date relance</span>
                <span className="modal-value">
                  {new Date(detailModal.dateRelance).toLocaleDateString('fr-FR', {
                    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
                  })}
                </span>
              </div>

              {(detailModal.formations_nom || (detailModal.formationsList && detailModal.formationsList.length > 0)) && (
                <div className="modal-row">
                  <span className="modal-label">
                    <i className="fa-solid fa-graduation-cap"></i> Formation(s)
                  </span>
                  <span className="modal-value">
                    {detailModal.formationsList && detailModal.formationsList.length > 0 ? (
                      <div className="modal-formations-list">
                        {detailModal.formationsList.map((f, idx) => (
                          <span key={f.id || idx} className="modal-formation-badge">
                            {f.intitule || f.label || f}
                          </span>
                        ))}
                      </div>
                    ) : (
                      detailModal.formations_nom
                    )}
                  </span>
                </div>
              )}

              {detailModal.commentaire && (
                <div className="modal-row">
                  <span className="modal-label"><i className="fa-regular fa-comment"></i> Commentaire</span>
                  <span className="modal-value">{detailModal.commentaire}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {detailModal.statutCalc !== 'fait' && (
                <button
                  type="button"
                  className="btn-ok-from-detail"
                  onClick={() => { setDetailModal(null); openOkPrompt(detailModal); }}
                >
                  <i className="fa-solid fa-phone-volume"></i> Appel effectué
                </button>
              )}
              <button type="button" className="btn-goto-prospect" onClick={() => handleNavigate(detailModal)}>
                <i className="fa-solid fa-arrow-right"></i> Voir la fiche
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ POPUP OK ════ */}
      {showOkPrompt && (
        <div className="modal-overlay show" onClick={() => setShowOkPrompt(false)}>
          <div className="relance-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="relance-modal-header">
              <div className="relance-modal-icon ok-icon">
                <i className="fa-solid fa-phone-volume"></i>
              </div>
              <h3 className="relance-modal-title">Échange effectué</h3>
              <button type="button" className="relance-modal-close" onClick={() => setShowOkPrompt(false)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="relance-modal-body">
              {pendingRelance && (
                <p className="ok-prompt-info">
                  <i className="fa-solid fa-circle-info" style={{ color: '#3b82f6' }}></i>{' '}
                  Échange pour{' '}
                  <strong>
                    {pendingRelance.type === 'prospect'
                      ? `${pendingRelance.prospectPrenom} ${pendingRelance.prospectNom}`
                      : pendingRelance.type === 'etudiant'
                      ? `${pendingRelance.etudiantPrenom} ${pendingRelance.etudiantNom}`
                      : `${pendingRelance.diplomePrenom} ${pendingRelance.diplomeNom}`}
                  </strong>
                </p>
              )}
              <div className="relance-field">
                <label className="relance-label">
                  <i className="fa-solid fa-phone"></i> Type d'échange
                </label>
                <div className="echange-type-radios">
                  {TYPE_ECHANGE_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`echange-type-radio ${okTypeEchange === opt.value ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="ok-type-home"
                        value={opt.value}
                        checked={okTypeEchange === opt.value}
                        onChange={() => setOkTypeEchange(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="relance-field">
                <label className="relance-label">
                  <i className="fa-solid fa-fire"></i> Niveau d'intérêt
                  <span className="optional-tag">optionnel</span>
                </label>
                <select
                  className="relance-input"
                  value={okNotes}
                  onChange={(e) => setOkNotes(e.target.value)}
                >
                  {NOTES_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="relance-field">
                <label className="relance-label">
                  <i className="fa-regular fa-comment"></i> Résumé de l'échange
                  <span className="optional-tag">optionnel</span>
                </label>
                <textarea
                  className="relance-textarea"
                  placeholder="Ex : Le prospect demande les prix, souhaite rappeler la semaine prochaine…"
                  rows={3}
                  value={okContenu}
                  onChange={(e) => setOkContenu(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="relance-modal-footer">
              <button type="button" className="btn-relance-cancel" onClick={() => setShowOkPrompt(false)}>
                Annuler
              </button>
              <button
                type="button"
                className="btn-relance-save btn-ok"
                onClick={confirmOk}
                disabled={savingId !== null}
              >
                {savingId !== null
                  ? <><i className="fa-solid fa-spinner fa-spin"></i> Enregistrement…</>
                  : <><i className="fa-solid fa-check-double"></i> Confirmer</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REPROGRAMMATION */}
      <RelanceModal
        isOpen={reprogModal}
        onClose={() => { setReprogModal(false); setReprogData(null); }}
        onSave={handleReprog}
        loading={modalLoading}
        title="Programmer une autre relance ?"
        formations={reprogData?.formations || []}
      />

      {/* MODAL CONFIRMATION SUPPRESSION */}
      {deleteConfirmId && (
        <div className="modal-overlay show" onClick={() => setDeleteConfirmId(null)}>
          <div className="modal-card delete-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: '#FEF2F2', color: '#EF4444', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                  <i className="fa-solid fa-trash"></i>
                </span>
                Supprimer la relance
              </h3>
              <button type="button" className="modal-close" onClick={() => setDeleteConfirmId(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px 24px 8px' }}>
              <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ color: '#F97316', marginRight: 6 }}></i>
                Êtes-vous sûr de vouloir supprimer cette relance ?
              </p>
              <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 6 }}>Cette action est irréversible.</p>
            </div>
            <div className="modal-footer" style={{ gap: 10 }}>
              <button type="button" className="btn-cancel-delete" onClick={() => setDeleteConfirmId(null)}>
                Annuler
              </button>
              <button type="button" className="btn-confirm-delete" onClick={confirmDelete}>
                <i className="fa-solid fa-trash"></i> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}